// Admin → Customers: what a shown name's save does to the routing windows, asked before anything is
// saved (`renameImpact`, `routing-rules.ts`). Customers sharing a shown name share one routing
// window — the strictest of theirs, or the app's rule of the group — so a save that puts codes with
// different windows together, or takes a code out of the group whose window it used, says so first:
// keep going (names only), set the group's own window (a `routing-rule` per code, then the names),
// keep the renamed codes' own windows (each kept separate from the group), keep the group's window
// for a code that leaves, or Cancel (nothing saved). A group a code leaves whose window changes for
// the codes left in it is a warning in the same dialog, never a block; alone, Continue / Cancel.

import { useRef, useState } from "react";
import { checkRule, hasRule, inUseText, pinnedText, ruleText, ruleWords, sameRule, shiftText } from "@app/shared";
import type { RenameImpact, RoutingRule, RuleShift } from "@app/shared";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from "@axny/platform-ui";
import { useDialogDrag } from "./dialog-drag";
import { useMessage } from "./message";
import { SourceBadge } from "./source-filter";

/** The person's answer: names only, the group's own window first, a leaving code keeps the group's; null: Cancel. */
export type RuleCheckChoice =
  | { kind: "names" }
  | { kind: "set"; rule: RoutingRule; noRule: boolean; notes: string }
  | { kind: "own" }
  | { kind: "keep" };

/** The dialog, and `ask` — what the person chose for an impact (`dialog` not "none"). */
export function useRuleCheck() {
  const [impact, setImpact] = useState<RenameImpact | null>(null);
  const resolve = useRef<(c: RuleCheckChoice | null) => void>(() => {});
  const ask = (i: RenameImpact) =>
    new Promise<RuleCheckChoice | null>((done) => {
      resolve.current = done;
      setImpact(i);
    });
  const close = (c: RuleCheckChoice | null) => {
    setImpact(null);
    resolve.current(c);
  };
  return { ask, dialog: impact ? <RuleCheckDialog impact={impact} onClose={close} /> : null };
}

/** Where a code's rule comes from, in a word: app or A2000 (their group's, or borrowed by name). */
const fromWord = (s: RuleShift["before"]) =>
  s.from === "app" || s.from === "app-group" ? "app" : s.from === "none" ? "" : "A2000";

function RuleCheckDialog({ impact, onClose }: { impact: RenameImpact; onClose: (c: RuleCheckChoice | null) => void }) {
  const drag = useDialogDrag();
  const message = useMessage();
  const share = impact.dialog === "share" && impact.joined;
  const strictest = impact.joined?.strictest ?? { openBdays: null, dueBdays: null };
  const [setting, setSetting] = useState(false);
  const [typed, setTyped] = useState({
    open: strictest.openBdays === null ? "" : String(strictest.openBdays),
    due: strictest.dueBdays === null ? "" : String(strictest.dueBdays),
    notes: "",
    none: false,
  });

  const saveSet = async () => {
    if (typed.none)
      return onClose({
        kind: "set",
        rule: { openBdays: null, dueBdays: null },
        noRule: true,
        notes: typed.notes.trim(),
      });
    const check = checkRule(typed.open, typed.due);
    if (!check.ok) return void (await message.error("Check the routing window", check.message));
    if (!hasRule(check.rule))
      return void (await message.error("Type Window opens or Route by", "Or tick No routing rule."));
    if (
      check.warning &&
      !(await message.confirm(check.warning.title, check.warning.text, { confirmLabel: "Save it anyway" }))
    )
      return;
    onClose({ kind: "set", rule: check.rule, noRule: false, notes: typed.notes.trim() });
  };

  // What the codes count with after the save: one window for all, said with where it comes from.
  const after = impact.joined?.members.map((m) => m.after) ?? [];
  const one = after.length && after.every((a) => sameRule(a, after[0]!)) ? after[0]! : null;
  const others = impact.shifts.filter((s) => !impact.joined?.members.some((m) => m.key === s.key));
  const pinned = impact.keepOwn.filter((k) => k.pinned);
  const warnOnly = impact.dialog === "warn";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose(null)}>
      <DialogContent
        ref={drag.ref}
        style={drag.style}
        initialFocus={drag.ref}
        className="max-h-[90dvh] w-[calc(100vw-2rem)] overflow-x-hidden overflow-y-auto sm:max-w-3xl"
      >
        <DialogHeader {...drag.handle}>
          <DialogTitle>
            {share
              ? "These customers will share one routing window"
              : warnOnly
                ? "A group's routing window changes"
                : "A routing window changes with this name"}
          </DialogTitle>
          <DialogDescription>
            {share
              ? `Customers with the same shown name (“${impact.joined!.shown}”) count with one routing window. Nothing is saved yet.`
              : warnOnly
                ? "The customers left in a group count with a different window once this one leaves it. Nothing is saved yet."
                : "Taking a customer out of its shown name changes the routing window it counts with. Nothing is saved yet."}
          </DialogDescription>
        </DialogHeader>
        {share ? (
          <>
            <ShiftTable rows={impact.joined!.members} />
            <p className="text-sm" data-testid="rule-check-result">
              <span className="font-medium">All will use: </span>
              {one ? ruleWords(one) : "their own rules (Mixed)"}
              {one && hasRule(one)
                ? sameRule(one, strictest)
                  ? " (the strictest)"
                  : one.from === "app" || one.from === "app-group"
                    ? " (the app rule of the group)"
                    : ""
                : ""}
            </p>
          </>
        ) : warnOnly ? null : (
          <ul className="space-y-1 text-sm" data-testid="rule-check-leave">
            {impact.leaving.map((s) => (
              <li key={s.key}>
                <SourceBadge source={s.source} /> <span className="font-mono">{s.code}</span> will no longer use{" "}
                {s.before.fromCodes.length && s.before.from !== "a2000" && s.before.from !== "app"
                  ? `${s.before.fromCodes.join(", ")}'s`
                  : "the group's"}{" "}
                routing window ({ruleText(s.before)}); it goes back to {hasRule(s.after) ? "" : "none"}
                {hasRule(s.after)
                  ? `${fromWord(s.after) === "app" ? "the app's" : "its own: A2000"} ${ruleText(s.after)}`
                  : ""}
                .
              </li>
            ))}
          </ul>
        )}
        {share && others.length ? (
          <p className="text-muted-foreground text-xs">
            Also changes: {others.map((s) => `${s.code} ${shiftText(s)}`).join(" · ")}
          </p>
        ) : null}
        {impact.leftBehind.length ? (
          <ul
            className="space-y-1 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900"
            data-testid="rule-check-left-behind"
          >
            {impact.leftBehind.map((g) => (
              <li key={g.shown}>{g.text}</li>
            ))}
          </ul>
        ) : null}
        {share && !setting && pinned.length ? (
          <div className="text-muted-foreground space-y-0.5 text-xs" data-testid="rule-check-keep-own">
            <p className="font-medium">With Keep their own routing windows:</p>
            {pinned.map((k) => (
              <p key={k.key}>{pinnedText(k)}</p>
            ))}
          </div>
        ) : null}
        {setting ? (
          <div className="space-y-2 rounded-md border p-3" data-testid="rule-check-set">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span>Window opens</span>
              <Input
                className="h-7 w-16 text-right tabular-nums"
                aria-label="Window opens (business days)"
                inputMode="numeric"
                maxLength={3}
                disabled={typed.none}
                value={typed.open}
                onChange={(e) => setTyped((t) => ({ ...t, open: e.target.value }))}
              />
              <span className="text-muted-foreground">business days before First ship date ·</span>
              <span>Route by</span>
              <Input
                className="h-7 w-16 text-right tabular-nums"
                aria-label="Route by (business days)"
                inputMode="numeric"
                maxLength={3}
                disabled={typed.none}
                value={typed.due}
                onChange={(e) => setTyped((t) => ({ ...t, due: e.target.value }))}
              />
              <span className="text-muted-foreground">before Cancel date</span>
            </div>
            <label className="flex items-center gap-1.5 text-sm">
              <Checkbox checked={typed.none} onCheckedChange={(c) => setTyped((t) => ({ ...t, none: !!c }))} />
              No routing rule
            </label>
            <Input
              placeholder="Notes (why, who asked)"
              maxLength={500}
              value={typed.notes}
              onChange={(e) => setTyped((t) => ({ ...t, notes: e.target.value }))}
            />
            <p className="text-muted-foreground text-xs">
              Saved on every code of “{impact.joined?.shown}” — {impact.joined?.members.length} — then the shown names.
            </p>
          </div>
        ) : null}
        <DialogFooter className="flex-wrap gap-2">
          {share ? (
            setting ? (
              <Button onClick={() => void saveSet()}>Save the window and the names</Button>
            ) : (
              <>
                <Button onClick={() => onClose({ kind: "names" })}>Apply with the strictest</Button>
                <Button variant="outline" onClick={() => setSetting(true)}>
                  Set the routing window…
                </Button>
                <Button
                  variant="outline"
                  title="Save the names; each renamed code keeps its own window, kept separate from the group"
                  onClick={() => onClose({ kind: "own" })}
                >
                  Keep their own routing windows
                </Button>
              </>
            )
          ) : (
            <>
              <Button onClick={() => onClose({ kind: "names" })}>Continue</Button>
              {warnOnly ? null : (
                <Button variant="outline" onClick={() => onClose({ kind: "keep" })}>
                  Keep the group's window for {impact.leaving.length === 1 ? "it" : "them"}
                </Button>
              )}
            </>
          )}
          <Button variant="outline" onClick={() => onClose(null)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** One row per code: its window now, where it comes from, and what changes. */
function ShiftTable({ rows }: { rows: readonly RuleShift[] }) {
  return (
    <table className="w-full table-fixed text-[13px] [&_td]:break-words" data-testid="rule-check-table">
      <thead className="text-muted-foreground text-left text-xs">
        <tr className="[&>th]:py-1 [&>th]:pr-3 [&>th]:font-medium">
          <th>Code</th>
          <th>Name</th>
          <th>Its routing window now</th>
          <th>Change</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key} className="border-t [&>td]:py-1 [&>td]:pr-3">
            <td className="whitespace-nowrap">
              <SourceBadge source={r.source} /> <span className="font-mono">{r.code}</span>
            </td>
            <td className="truncate">{r.name}</td>
            <td>{hasRule(r.before) ? inUseText(r.before) : "— (none)"}</td>
            <td className="text-muted-foreground">{sameRule(r.before, r.after) ? "unchanged" : shiftText(r)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
